import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import dayjs from 'dayjs'

import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import useDebounce from '@/hooks/useDebounce'
import { useAwaitingRatings } from '@/modules/InternalRating/hooks/use-internal-rating'
import { AwaitingRatingFilters } from '@/modules/InternalRating/types'

import AwaitingFilters from './AwaitingFilters'

const PAGE_SIZE = 20

// Tab "Chờ tôi đánh giá": mỗi dòng deep-link tới /tickets/:id?rate=1 (mở dialog chấm).
// W-UI (30/07): thêm "chấm cho AI" (tên người, không chỉ phòng ban), nhãn cho mốc thời
// gian, và hạn đánh giá lấy từ task nhắc. Hạn KHÔNG suy từ completed_at — cột đó là
// COALESCE(ticket_resolved_at, ...) mà ticket_resolved_at DEFAULT về thời điểm TẠO ticket.
// W-UI2 (30/07): filter theo mã/nội dung, loại ticket, và chỉ-quá-hạn.
const AwaitingList = () => {
    const { t } = useTranslation('rating')
    const [page, setPage] = useState(1)

    const [searchInput, setSearchInput] = useState('')
    const [typeInput, setTypeInput] = useState('')
    const [overdueOnly, setOverdueOnly] = useState(false)

    // Debounce: ILIKE '%x%' không dùng được index nên mỗi ký tự = một lần quét.
    const search = useDebounce(searchInput, 400)
    const ticketType = useDebounce(typeInput, 400)

    const filters = useMemo<AwaitingRatingFilters>(
        () => ({
            search: search || undefined,
            ticket_type: ticketType || undefined,
            overdue_only: overdueOnly || undefined,
        }),
        [search, ticketType, overdueOnly]
    )

    // Suy từ giá trị ĐÃ debounce, không phải raw input — nếu không, bấm "Xoá lọc" sẽ
    // hiện ngay empty state sai trong 400ms trước khi query kịp refetch.
    const hasActive = !!search || !!ticketType || overdueOnly

    const { data, renderState, isFetching } = useAwaitingRatings(
        page,
        PAGE_SIZE,
        filters
    )

    // Reset page NGAY trong handler, không dùng useEffect — xem giải thích chi tiết ở
    // ReceivedList (useBaseQuery áp option mới trong useEffect nên reset bằng effect làm
    // fetch thêm một lần với page CŨ + filter MỚI).
    const onPage1 =
        <T,>(set: (v: T) => void) =>
        (v: T) => {
            setPage(1)
            set(v)
        }

    const reset = () => {
        setPage(1)
        setSearchInput('')
        setTypeInput('')
        setOverdueOnly(false)
    }

    // KHÔNG early-return trên state: lỗi API sẽ xoá luôn filter bar và người dùng
    // không còn cách bỏ bộ lọc đang gây lỗi. Render filter trước, state vào chỗ list.
    const state = renderState()
    const items = data?.items ?? []

    return (
        <div className="space-y-3">
            <AwaitingFilters
                searchInput={searchInput}
                onSearchInputChange={onPage1(setSearchInput)}
                typeInput={typeInput}
                onTypeInputChange={onPage1(setTypeInput)}
                overdueOnly={overdueOnly}
                onOverdueOnlyChange={onPage1(setOverdueOnly)}
                onReset={reset}
                hasActive={hasActive}
            />

            {state ? (
                state
            ) : !items.length ? (
                <p className="py-10 text-center text-sm text-gray-400">
                    {hasActive
                        ? t('page.empty_filtered')
                        : t('page.empty_awaiting')}
                </p>
            ) : (
                <>
                    {/* Số dòng của TRANG hiện tại, '+' khi còn trang sau. Cố ý không
                        gọi là tổng: API dùng has_more chứ không trả total (tránh COUNT). */}
                    <p className="text-sm text-gray-500">
                        {t('page.awaiting_count', { count: items.length })}
                        {data?.has_more ? '+' : ''}
                    </p>

                    {items.map((item) => {
                        const due = item.rating_due_at
                            ? dayjs(item.rating_due_at)
                            : null
                        const overdue = due ? dayjs().isAfter(due) : false
                        // Tên người là thông tin chính; phòng ban là phụ. Khi BE chưa
                        // deploy (undefined) thì rơi về hiển thị phòng ban như cũ.
                        const people = item.rated_employee_names ?? []

                        return (
                            <Link
                                key={item.ticket_id}
                                to={`/tickets/${item.ticket_id}?rate=1`}
                                className="flex items-center justify-between gap-3 rounded-lg border border-gray-100 p-3 transition-colors hover:border-primary/40 hover:bg-primary/5"
                            >
                                <div className="min-w-0 space-y-1">
                                    <div className="truncate text-sm font-medium text-gray-800">
                                        #{item.ticket_id}
                                        {item.ticket_content
                                            ? ` — ${item.ticket_content}`
                                            : ''}
                                    </div>

                                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-400">
                                        {item.ticket_type && (
                                            <span>{item.ticket_type}</span>
                                        )}
                                        {item.completed_at && (
                                            <span>
                                                {t('page.completed_at')}:{' '}
                                                {dayjs(
                                                    item.completed_at
                                                ).format('DD/MM/YYYY')}
                                            </span>
                                        )}
                                        {due && (
                                            <span
                                                className={
                                                    overdue
                                                        ? 'font-medium text-amber-600'
                                                        : ''
                                                }
                                            >
                                                {t('page.due_at')}:{' '}
                                                {due.format('DD/MM/YYYY HH:mm')}
                                                {overdue
                                                    ? ` · ${t('page.overdue')}`
                                                    : ''}
                                            </span>
                                        )}
                                        {!!item.reminder_count && (
                                            <span>
                                                {t('page.reminded', {
                                                    count: item.reminder_count,
                                                })}
                                            </span>
                                        )}
                                    </div>

                                    {/* "Chấm cho ai" — người trước, phòng ban sau. */}
                                    {!!people.length && (
                                        <div className="text-xs text-gray-600">
                                            <span className="text-gray-400">
                                                {t('page.rate_for')}:{' '}
                                            </span>
                                            <span className="font-medium text-gray-700">
                                                {people.join(', ')}
                                            </span>
                                        </div>
                                    )}
                                    {!!item.rated_department_names?.length && (
                                        <div className="flex flex-wrap gap-1">
                                            {item.rated_department_names.map(
                                                (d) => (
                                                    <Badge
                                                        key={d}
                                                        variant="secondary"
                                                    >
                                                        {d}
                                                    </Badge>
                                                )
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* span, không phải <Button>: nút bấm lồng trong <a> là
                                    HTML không hợp lệ và tạo 2 tab-stop cho 1 hành động. */}
                                <span className="shrink-0 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-white">
                                    {t('page.rate_now')}
                                </span>
                            </Link>
                        )
                    })}
                </>
            )}

            {!state && (
                <div className="flex items-center justify-center gap-2 pt-1">
                    <Button
                        variant="outline"
                        size="sm"
                        disabled={page <= 1 || isFetching}
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                        {t('page.prev')}
                    </Button>
                    <span className="text-xs text-gray-400">{page}</span>
                    <Button
                        variant="outline"
                        size="sm"
                        disabled={!data?.has_more || isFetching}
                        onClick={() => setPage((p) => p + 1)}
                    >
                        {t('page.next')}
                    </Button>
                </div>
            )}
        </div>
    )
}

export default AwaitingList
